import type { ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'
import { BellIcon } from './BellIcon'

/** Каркас страниц для родителя — упрощённый, без nav-меню. */
export function ParentShell({
  title,
  subtitle,
  children,
}: {
  title?: string
  subtitle?: string
  children: ReactNode
}) {
  const { teacher: parent, logout } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const handleLogout = async () => { await logout(); navigate('/') }

  if (!parent) return null

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
          <Link to="/parent" className="flex items-center gap-2 group flex-shrink-0">
            <img src="/edpad-logo.png" alt="EdPad" className="w-8 h-8 rounded transition-transform group-hover:scale-105" />
            <span className="font-semibold text-gray-900 hidden sm:inline">{t('parentShell.brand')}</span>
          </Link>

          <nav className="flex items-center gap-1 ml-3">
            <NavLink
              to="/parent"
              end
              className={({ isActive }) => `text-sm px-3 py-1.5 rounded-md transition-colors ${isActive ? 'bg-primary-soft text-primary font-medium' : 'text-gray-600 hover:text-primary hover:bg-gray-50'}`}
            >
              {t('parentShell.navChildren')}
            </NavLink>
            <NavLink
              to="/parent/chat"
              className={({ isActive }) => `text-sm px-3 py-1.5 rounded-md transition-colors ${isActive ? 'bg-primary-soft text-primary font-medium' : 'text-gray-600 hover:text-primary hover:bg-gray-50'}`}
            >
              💬 {t('parentShell.navChat')}
            </NavLink>
          </nav>

          <div className="flex-1" />

          <BellIcon />

          <div className="hidden md:block text-sm text-right truncate max-w-[180px]">
            <div className="font-medium truncate">{parent.displayName}</div>
            <div className="text-xs text-gray-500 truncate">{t('parentShell.roleParent')}</div>
          </div>
          <button onClick={handleLogout} className="text-sm text-gray-600 hover:text-red-600 px-2">
            {t('common.logout')}
          </button>
        </div>
      </header>

      {(title || subtitle) && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
            {title && <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h1>}
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
      )}

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-6 pb-40 md:pb-6">
        {children}
      </main>
    </div>
  )
}
